import { IconButton, TextButton } from '@creditornot/cb-button';
import { CaretLeft, CaretRight } from '@creditornot/cb-icons';
import { CaretDown } from '@creditornot/cb-icons';
import { Typography as T, cssThemedValue } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { BorderlessSelect } from './BorderlessSelect';
import { ColumnSelector } from './ColumnSelector';
import { SORT_BY_OPTIONS, STATUS_OPTIONS, ALL_DATA_TABLE_COLUMNS_OPTIONS } from './constants';
import { DataTableState } from './useDataTable';

type DataTableControlsProps = {
  state: DataTableState;
};

const Root = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 16px;
  align-items: center;
  gap: 20px;
`;

const ArrowIconButton = styled(IconButton).attrs({
  size: 'small',
  variant: 'grey',
  alignment: 'boundingBox',
})``;

const LeftArrowIcon = styled(CaretLeft).attrs({
  width: 10,
  height: 10,
})``;

const RightArrowIcon = styled(CaretRight).attrs({
  width: 10,
  height: 10,
})``;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const PageRange = styled(T.Body2)`
  color: ${cssThemedValue('textSecondary')};
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 8px;
`;

export const DataTableControls = ({ state }: DataTableControlsProps) => {
  const {
    pagination: { hasNextPage, hasPreviousPage, goToNextPage, goToPreviousPage, currentRange, listLength },
  } = state;
  return (
    <Root>
      <FilterContainer>
        <T.Body3>
          <FormattedMessage id="shared-menu.editor.importer.table-view.sort-by" />
        </T.Body3>
        <BorderlessSelect
          variant="blue"
          options={SORT_BY_OPTIONS}
          value={state.appliedSortBy}
          onChange={state.setAppliedSortBy}
        />
      </FilterContainer>
      <FilterContainer>
        <T.Body3>
          <FormattedMessage id="shared-menu.editor.importer.table-view.change-status" />
        </T.Body3>
        <BorderlessSelect
          variant="blue"
          options={STATUS_OPTIONS}
          value={state.appliedStatusFilter}
          onChange={state.setAppliedStatusFilter}
        />
      </FilterContainer>
      <FilterContainer>
        <T.Body3>
          <FormattedMessage id="shared-menu.editor.importer.table-view.choose-columns" />
        </T.Body3>
        <Popover
          placement="bottom-start"
          showOnHover={false}
          showOnClick
          renderArrow={false}
          disableContentPadding
          content={
            <ColumnSelector
              variant="blue"
              options={ALL_DATA_TABLE_COLUMNS_OPTIONS[state.currentView]}
              selectedColumns={state.columnsToDisplay[state.currentView]}
              onChange={selectedColumns => {
                state.setColumnsToDisplay(prevState => ({
                  ...prevState,
                  [state.currentView]: selectedColumns,
                }));
              }}
              onSelectAll={() => {
                state.setColumnsToDisplay(prevState => ({
                  ...prevState,
                  [state.currentView]: ALL_DATA_TABLE_COLUMNS_OPTIONS[state.currentView],
                }));
              }}
              onDeselectAll={() => {
                state.setColumnsToDisplay(prevState => ({
                  ...prevState,
                  [state.currentView]: ALL_DATA_TABLE_COLUMNS_OPTIONS[state.currentView].filter(
                    ({ disabled }) => disabled,
                  ),
                }));
              }}
            />
          }
        >
          <TextButton variant="blue" icon={<CaretDown />} iconPosition="right">
            <FormattedMessage
              id="shared-menu.editor.importer.table-view.num-of-columns-selected"
              values={{
                num: state.columnsToDisplay[state.currentView].length,
              }}
            />
          </TextButton>
        </Popover>
      </FilterContainer>

      <PageRange>
        <FormattedMessage
          id="shared-menu.editor.importer.table-view.pagination-range"
          values={{
            currentRange,
            listLength,
          }}
        />
      </PageRange>
      <ButtonsContainer>
        <ArrowIconButton disabled={!hasPreviousPage} onClick={goToPreviousPage}>
          <LeftArrowIcon />
        </ArrowIconButton>
        <ArrowIconButton disabled={!hasNextPage} onClick={goToNextPage}>
          <RightArrowIcon />
        </ArrowIconButton>
      </ButtonsContainer>
    </Root>
  );
};
